import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import Card from 'assets/components/card/Card';
import ThemeCardListItem from 'assets/components/card/themes/Theme.Card.ListItem';
import { addLog } from 'assets/components/feedback/Feedback';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import TextBox, { textBoxStyles } from 'assets/components/textBox/TextBox';
import { getLocales } from 'assets/locales/Locale';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import { LocationSearch } from 'assets/templates/locationSearch/LocationSearch';
import Theme from 'assets/themes/Theme.Common';
import { formatFormPhone } from 'assets/utils/forms/formatFormPhone';
import { isEmail, isValidPhoneNumber } from 'assets/utils/parsersAndValidation/Validators';
import { values } from 'lodash';
import PaymentType from 'models/enums/PaymentType';
import ContractRefundPayee from 'models/sales/contractRefundPayee/Model.ContractRefundPayee';
import RefundPayee from 'models/finance/refundPayee/Model.RefundPayee';
import { ContractRefundForm, useContractRefundProvider } from '../../../Sales.ContractRefund.Hooks';

export const refundCardStyles = {
  borderRadius: Theme.Size.S,
  cardMedia: {
    width: 0,
  },
  cardInfo: {
    paddingRight: Theme.Size.M,
  },
};
const SalesContractRefundProcessPayees = () => {
  const { lang, translate } = getLocales();
  const { form } = useModalForm<ContractRefundForm>();
  const { client, contractDealer, lender } = useContractRefundProvider();

  const addPayee = () => {
    const newPayee = {
      ...new ContractRefundPayee({
        payee: new RefundPayee({}),
        isPrimary: false,
        paymentMethodCode: undefined,
      }),
    };
    form.merge({
      contractRefundPayees: [...(form.data.contractRefundPayees ?? []), newPayee],
    });
  };
  const deletePayee = (index: number) => {
    if (index === 0) {
      addLog({ warning: lang.cannotBeDeleted });
      return;
    }
    const updatedpayees = form.data?.contractRefundPayees?.filter((_, idx) => idx !== index);
    form.update({ contractRefundPayees: updatedpayees });
  };
  const updateContractRefundPayee = (index: number, updated: Partial<ContractRefundPayee>) => {
    const updatedPayees = (form.data?.contractRefundPayees ?? []).map((it, idx) =>
      idx === index
        ? new ContractRefundPayee({
            ...it,
            ...updated,
          })
        : it
    );

    form.merge({
      contractRefundPayees: updatedPayees,
    });
  };

  const updatePayee = (index: number, updated: Partial<RefundPayee>) => {
    const updatedPayees = (form.data?.contractRefundPayees ?? []).map((it, idx) =>
      idx === index
        ? new ContractRefundPayee({
            ...it,
            payee: new RefundPayee({
              ...it.payee,
              ...updated,
            }),
          })
        : it
    );

    form.merge({
      contractRefundPayees: updatedPayees,
    });
  };

  const overridePrimaryPayee = ({ payeeType }: { payeeType: 'client' | 'dealer' | 'lender' }) => {
    const payees = form.data?.contractRefundPayees ?? [];

    let newPrimary: ContractRefundPayee;
    if (payeeType === 'client') {
      newPrimary = client?.toContractRefundPayee();
    }
    if (payeeType === 'dealer') {
      newPrimary = contractDealer?.toContractRefundPayee();
    }
    if (payeeType === 'lender') {
      newPrimary = lender?.toContractRefundPayee();
    }

    if (!newPrimary) return;

    form.merge({
      contractRefundPayees: [newPrimary, ...payees.slice(1)],
    });
  };

  return (
    <FormWrapper style={{ padding: `${Theme.Size.S} ${Theme.Size.XL} !important` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          <strong>{lang.payeeInformation}</strong>
        </span>
        <DropdownButton
          title={lang.addPayee}
          buttonBody={lang.addPayee}
          buttonMedia={<Icon class="fas-plus" />}
          horizontalPosition="auto_fixed"
          verticalPosition="auto_fixed"
          subcomponents={ThemeDropdownButtonPopup}
          dropdownOptions={
            <>
              <Button
                class={ThemeButtonMenuItem}
                onClick={() => overridePrimaryPayee({ payeeType: 'client' })}
                render={lang.client}
                disabled={!client}
                htmlElementProps={{ title: `${lang.overridePrimaryPayeeWith} ${lang.client}` }}
                media={<Icon class="fas-user" />}
              />
              <Button
                class={ThemeButtonMenuItem}
                onClick={() => overridePrimaryPayee({ payeeType: 'dealer' })}
                render={lang.dealer}
                disabled={!contractDealer}
                htmlElementProps={{ title: `${lang.overridePrimaryPayeeWith} ${lang.dealer}` }}
                media={<Icon class="fas-user" />}
              />
              <Button
                class={ThemeButtonMenuItem}
                onClick={() => overridePrimaryPayee({ payeeType: 'lender' })}
                render={lang.lender}
                media={<Icon class="fas-user" />}
                htmlElementProps={{
                  title: !lender
                    ? `${lang.lender} ${lang.notAvailable}`
                    : `${lang.overridePrimaryPayeeWith} ${lang.lender}`,
                }}
                disabled={!lender}
              />
              <Button
                class={ThemeButtonMenuItem}
                onClick={addPayee}
                render={lang.addNew}
                media={<Icon class="fas-plus" />}
              />
            </>
          }
        />
      </div>
      {form.data.contractRefundPayees?.map((it, idx) => {
        const primaryPayee = it.client || it.dealer || it.lender;
        const payee = it.payee;
        return (
          <Card
            key={primaryPayee?.name}
            subtitle={() => {
              const isPrimaryPayee = it?.isPrimary || idx === 0;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isPrimaryPayee ? 'space-between' : 'flex-end',
                    }}
                  >
                    {isPrimaryPayee && (
                      <p>
                        <strong>{`${lang.requestor} (${lang.primaryPayee})`}</strong>
                      </p>
                    )}
                    <Button
                      class={ThemeButtonCircle}
                      data={idx}
                      onClick={deletePayee}
                      media={<Icon class="fas-trash" />}
                      disabled={isPrimaryPayee}
                    />
                  </div>
                  &nbsp;
                  <div>
                    {isPrimaryPayee ? (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: Theme.Size.L,
                          fontSize: Theme.Size.M,
                        }}
                      >
                        {it?.client || it?.clientId ? (
                          <>
                            <TextBox
                              class={textBoxStyles.block.flexBlock}
                              title={lang.firstName}
                              subtitle={it?.client?.firstName}
                            />
                            <TextBox
                              class={textBoxStyles.block.flexBlock}
                              title={lang.lastName}
                              subtitle={it?.client?.lastName}
                            />
                          </>
                        ) : (
                          <TextBox
                            class={textBoxStyles.block.flexBlock}
                            title={lang.name}
                            subtitle={primaryPayee?.name}
                          />
                        )}
                        <TextBox
                          class={textBoxStyles.block.flexBlock}
                          title={lang.email}
                          subtitle={primaryPayee?.email}
                        />
                        <TextBox
                          class={textBoxStyles.block.flexBlock}
                          title={lang.phoneNumber}
                          subtitle={primaryPayee?.phoneNumber}
                        />
                        <TextBox
                          class={textBoxStyles.block.flexBlock}
                          title={lang.streetAddress}
                          subtitle={primaryPayee?.streetAddress}
                        />
                        <TextBox
                          class={textBoxStyles.block.flexBlock}
                          title={lang.city}
                          subtitle={primaryPayee?.city}
                        />
                        <TextBox
                          class={textBoxStyles.block.flexBlock}
                          title={lang.postalCode}
                          subtitle={primaryPayee?.postalCode}
                        />
                        <TextBox
                          class={textBoxStyles.block.flexBlock}
                          title={lang.provinceCode}
                          subtitle={primaryPayee?.provinceCode}
                        />
                        <TextBox
                          class={textBoxStyles.block.flexBlock}
                          title={lang.paymentMethod}
                          subtitle={
                            <SelectInput
                              name="paymentMethodCode"
                              data={values(PaymentType).map((it) => ({
                                id: it,
                                title: translate(it),
                              }))}
                              value={it.dealer?.paymentMethodCode || it.paymentMethodCode}
                              onChange={({ paymentMethodCode }) =>
                                updateContractRefundPayee(idx, {
                                  paymentMethodCode,
                                })
                              }
                              disabled={!!it.dealer?.paymentMethodCode}
                              error={!it.paymentMethodCode && lang.required}
                            />
                          }
                        />
                        {(it.client || it.clientId) && (
                          <TextBox
                            class={textBoxStyles.block.flexBlock}
                            title={lang.taxExempt}
                            subtitle={it.client?.taxExempt ? <Icon class="fas-check" /> : <Icon class="fas-times" />}
                          />
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: Theme.Size.L,
                          paddingBottom: Theme.Size.M,
                        }}
                      >
                        <InputWrapper>
                          <TextInput
                            name="firstName"
                            label={lang.firstName}
                            value={payee?.firstName}
                            error={!payee?.firstName && lang.required}
                            onChange={({ firstName }) => updatePayee(idx, { firstName })}
                            icon={<Icon class="if-quill-pen" />}
                          />
                          <TextInput
                            name="lastName"
                            label={lang.lastName}
                            value={payee?.lastName}
                            error={!payee?.lastName && lang.required}
                            onChange={({ lastName }) => updatePayee(idx, { lastName })}
                            icon={<Icon class="if-quill-pen" />}
                          />
                        </InputWrapper>
                        <InputWrapper>
                          <TextInput
                            name="email"
                            label={lang.email}
                            value={payee?.email}
                            error={
                              !payee?.email
                                ? lang.required
                                : !isEmail(payee?.email)
                                  ? lang.invalidEmailFormatRequires
                                  : undefined
                            }
                            onChange={({ email }) => updatePayee(idx, { email })}
                            icon={<Icon class="if-email" />}
                          />
                          <TextInput
                            name="phone"
                            label={lang.phoneNumber}
                            value={payee?.phone}
                            error={
                              !payee?.phone
                                ? lang.required
                                : !isValidPhoneNumber(payee?.phone)
                                  ? lang.invalidFormatRequires.replace('{format}', '###-###-####')
                                  : undefined
                            }
                            onChange={({ phone }) => updatePayee(idx, { phone: formatFormPhone(payee?.phone, phone) })}
                            icon={<Icon class="if-phone" />}
                          />
                        </InputWrapper>
                        <LocationSearch
                          onSelect={(location) => {
                            updatePayee(idx, {
                              streetAddress: location?.Line1 || null,
                              provinceCode: location?.ProvinceCode || null,
                              city: location?.City || null,
                              countryCode: location?.CountryIso3 || null,
                              postalCode: location?.PostalCode || null,
                            });
                          }}
                        />
                        <InputWrapper>
                          <TextInput
                            name="streetAddress"
                            label={lang.streetAddress}
                            value={payee?.streetAddress}
                            error={!payee?.streetAddress && lang.required}
                            onChange={({ streetAddress }) => updatePayee(idx, { streetAddress })}
                            icon={<Icon class="if-map-pin" />}
                          />
                          <TextInput
                            name="postalCode"
                            label={lang.postalCode}
                            value={payee?.postalCode}
                            error={!payee?.postalCode && lang.required}
                            onChange={({ postalCode }) => updatePayee(idx, { postalCode })}
                            icon={<Icon class="if-map-pin" />}
                          />
                          <TextInput
                            name="city"
                            label={lang.city}
                            value={payee?.city}
                            error={!payee?.city && lang.required}
                            onChange={({ city }) => updatePayee(idx, { city })}
                            icon={<Icon class="if-map-pin" />}
                          />
                          <TextInput
                            name="provinceCode"
                            label={lang.provinceCode}
                            value={payee?.provinceCode}
                            error={!payee?.provinceCode && lang.required}
                            onChange={({ provinceCode }) => updatePayee(idx, { provinceCode })}
                            icon={<Icon class="if-map-pin" />}
                          />
                          <TextInput
                            name="countryCode"
                            label={lang.countryCode}
                            value={payee?.countryCode}
                            error={!payee?.countryCode && lang.required}
                            onChange={({ countryCode }) => updatePayee(idx, { countryCode })}
                            icon={<Icon class="if-map-pin" />}
                          />
                        </InputWrapper>
                        <CheckWrapper>
                          <CheckInput
                            style={{ alignSelf: 'center' }}
                            name="taxExempt"
                            class={inputStyles.check.switch}
                            label={lang.taxExempt}
                            value={payee?.taxExempt}
                            onChange={({ taxExempt }) => updatePayee(idx, { taxExempt: Boolean(taxExempt) })}
                            htmlElementProps={{ title: payee?.taxExempt ? lang.disable : lang.enable }}
                          />
                          <SelectInput
                            name="paymentMethodCode"
                            label={lang.paymentMethod}
                            data={values(PaymentType).map((it) => ({
                              id: it,
                              title: translate(it),
                            }))}
                            value={payee?.paymentMethodCode}
                            onChange={({ paymentMethodCode }) => {
                              updateContractRefundPayee(idx, {
                                paymentMethodCode,
                              });
                              updatePayee(idx, {
                                paymentMethodCode,
                              });
                            }}
                            error={!payee?.paymentMethodCode && lang.required}
                          />
                        </CheckWrapper>
                      </div>
                    )}
                  </div>
                </div>
              );
            }}
            class={ThemeCardListItem}
            style={refundCardStyles}
          />
        );
      })}
    </FormWrapper>
  );
};

export default SalesContractRefundProcessPayees;
